/**
 * sutun.ts
 *
 * NE İŞE YARAR : Tablo sütunlarını süzer — izni olmayan **ve** yanıtta bulunmayan sütun çizilmez
 * KAYNAK SPEC  : 15-arayuz-standardi.md §7.1 (*"buton, menü öğesi ve **sütun** hiç
 *                görünmez"*) · §7.4 (alan bazlı gizleme) · §8.2 (liste kuralları)
 *                03-alan-politikasi.md §6 · 04-api-sozlesmesi.md §2.8
 * BAĞLI OLDUĞU : src/yetki/alan.ts
 * KULLANAN     : Liste ekranları
 * DİKKAT       : 1) İKİ AYRI SÜZGEÇ VARDIR VE İKİSİ DE GEREKLİ:
 *                     izin  → rolün o sütunu görmeye hakkı var mı  (`15` §7.1)
 *                     alan  → sunucu o alanı gönderdi mi           (`15` §7.4)
 *                   Yalnız izne bakılsaydı, izni olan ama alan politikası gereği alanı
 *                   gelmeyen bir rol boş bir sütun görürdü. Yalnız alana bakılsaydı,
 *                   liste boş dönen bir sayfada bütün sütunlar kaybolurdu.
 *                2) ALAN VARLIĞI **İLK SATIRDAN** OKUNUR ve bu güvenlidir: profil
 *                   derleme zamanında role göre üretilir (`03` §6.1), yani bir yanıtın
 *                   bütün satırları **aynı** alan kümesini taşır. Satır satır bakmak
 *                   aynı sonucu verir ve her çizimde listeyi bir kez daha dolaşırdı.
 *                3) LİSTE BOŞSA SÜTUNLAR **KORUNUR**. Boş liste zaten *"boş durum"*
 *                   ekranıyla karşılanıyor (`15` §9); sütunları da silmek, kullanıcıya
 *                   verinin değil **yetkisinin** eksik olduğunu düşündürürdü.
 *                4) BAŞLIK **ANAHTAR**TIR, METİN DEĞİL (`15` §14). Çözümü tabloya
 *                   bırakıldı; sütun bildirimi hangi dilde olduğunu bilmez.
 *                5) BU BİR GÜVENLİK SINIRI DEĞİLDİR (`15` §7.3). Sütunu gizlemek veriyi
 *                   engellemez; veri zaten yanıtta yoktur — engelleyen şey sunucudaki
 *                   serileştiricidir. Burası yalnız ekranın ne çizeceğini söyler.
 * SON GÜNCELLEME: 2026-08-31 05:25:00
 */
import type { MetinAnahtari } from '../metin';
import { alanVar, type Kayit } from './alan';

export interface Sutun<T extends Kayit> {
  /** Yanıttaki alan adı — sütunun varlığı buna bakılarak belirlenir. */
  readonly ad: string;
  /**
   * Başlığın **sözlük anahtarı** (`15` §14 — kod içinde sabit metin yasak).
   *
   * Düz metin kabul edilseydi on beş liste ekranının sütun başlıkları koda dağılır ve
   * ikinci dil eklendiğinde hepsi tek tek aranırdı.
   */
  readonly baslik: MetinAnahtari;
  /** Gerekli izin kodu; `null` ise izin aranmaz (`15` §7.1). */
  readonly izin?: string | null;
  /** Hücreyi çizen fonksiyon; verilmezse değer düz metin yazılır. */
  readonly hucre?: (satir: T) => string;
  /** Sayı/tarih sütunu — sabit genişlikli rakam (`15` §4.2). */
  readonly sayi?: boolean;
}

/**
 * Sütunları süzer (başlıktaki not 1).
 *
 * `satirlar` boşsa alan süzgeci **uygulanmaz** (not 3).
 */
export function sutunlariSuz<T extends Kayit>(
  sutunlar: readonly Sutun<T>[],
  satirlar: readonly T[],
  izinVar: (kod: string) => boolean,
): readonly Sutun<T>[] {
  const ornek = satirlar[0];

  return sutunlar.filter((sutun) => {
    if (sutun.izin !== undefined && sutun.izin !== null && !izinVar(sutun.izin)) {
      return false;
    }
    // Başlıktaki notlar 2–3.
    return ornek === undefined || alanVar(ornek, sutun.ad);
  });
}
